import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  CampaignIcon,
  ClipboardIcon,
} from '../../../shared/ToolbarPrimitives';
import './styles.css';

const MENU_WIDTH = 200;
// Every row, including Paste, so the menu doesn't jump when the clipboard check finishes.
const MENU_HEIGHT = 16 + 5 * 42 + 9;

function clampMenu(screen) {
  let left = screen?.x || 0;
  let top = screen?.y || 0;
  if (left + MENU_WIDTH > window.innerWidth - 10) {
    left = window.innerWidth - MENU_WIDTH - 10;
  }
  if (left < 10) left = 10;
  if (top + MENU_HEIGHT > window.innerHeight - 10) {
    top = window.innerHeight - MENU_HEIGHT - 10;
  }
  if (top < 10) top = 10;
  return { left, top };
}

/**
 * Right-click menu for empty canvas space.
 * It stays in the page, hidden, and unhides during the press — same as the status menu —
 * so the browser can paint it before React renders.
 */
const CanvasContextMenu = forwardRef(function CanvasContextMenu({
  position = null,
  canvasPosition = null,
  onAddMission,
  onAddDecision,
  onAddWait,
  onCreateCampaign,
  onPaste,
  onClose,
  hasClipboardData = false,
}, ref) {
  const menuRef = useRef(null);
  const placeRef = useRef(canvasPosition || { x: 0, y: 0 });
  const stampRef = useRef(0);
  const [open, setOpen] = useState(position != null);
  const [anchor, setAnchor] = useState(() => (position ? clampMenu(position) : { left: 0, top: 0 }));
  const [clipboardAvailable, setClipboardAvailable] = useState(hasClipboardData);
  const showCreateCampaign = typeof onCreateCampaign === 'function';
  const shown = open || position != null;

  const dismiss = useCallback(() => {
    stampRef.current += 1;
    menuRef.current?.classList.add('canvas-context-menu--shut');
    setOpen(false);
  }, []);

  const refreshClipboard = useCallback(() => {
    navigator.clipboard?.readText?.().then((text) => {
      const data = JSON.parse(text);
      setClipboardAvailable(data?.fokus === 'v2' && Array.isArray(data?.tasks) && data.tasks.length > 0);
    }).catch(() => {
      setClipboardAvailable(false);
    });
  }, []);

  const finish = useCallback((action) => {
    action?.();
    dismiss();
    onClose?.();
  }, [dismiss, onClose]);

  useImperativeHandle(ref, () => ({
    reveal(screen, canvas) {
      stampRef.current += 1;
      const mine = stampRef.current;
      if (canvas) placeRef.current = canvas;
      const menu = menuRef.current;
      const pos = clampMenu(screen);
      if (menu) {
        menu.style.left = `${pos.left}px`;
        menu.style.top = `${pos.top}px`;
        menu.classList.remove('canvas-context-menu--shut');
      }
      refreshClipboard();
      window.setTimeout(() => {
        if (stampRef.current !== mine) return;
        setAnchor(pos);
        setOpen(true);
      }, 0);
    },
    dismiss,
  }), [dismiss, refreshClipboard]);

  useEffect(() => {
    refreshClipboard();
    window.addEventListener('focus', refreshClipboard);
    return () => window.removeEventListener('focus', refreshClipboard);
  }, [refreshClipboard]);

  useEffect(() => {
    if (!shown) return undefined;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        dismiss();
        onClose?.();
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        dismiss();
        onClose?.();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [shown, dismiss, onClose]);

  useEffect(() => {
    const handlePanStart = () => {
      const menu = menuRef.current;
      if (!menu || menu.classList.contains('canvas-context-menu--shut')) return;
      dismiss();
      onClose?.();
    };
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [dismiss, onClose]);

  const menu = (
    <div
      ref={menuRef}
      className={shown ? 'canvas-context-menu' : 'canvas-context-menu canvas-context-menu--shut'}
      style={{
        position: 'fixed',
        left: anchor.left,
        top: anchor.top,
        zIndex: 10000,
      }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="canvas-context-menu__list">
        <button
          className="canvas-context-menu__item"
          onClick={() => finish(() => onAddMission?.(placeRef.current))}
        >
          <span className="canvas-context-menu__item-icon"><MissionIcon /></span>
          <span className="canvas-context-menu__item-label">Create Mission</span>
        </button>

        <button
          className="canvas-context-menu__item"
          onClick={() => finish(() => onAddDecision?.(placeRef.current))}
        >
          <span className="canvas-context-menu__item-icon"><DiamondIcon /></span>
          <span className="canvas-context-menu__item-label">Create Decision</span>
        </button>

        <button
          className="canvas-context-menu__item"
          onClick={() => finish(() => onAddWait?.(placeRef.current))}
        >
          <span className="canvas-context-menu__item-icon"><HourglassIcon /></span>
          <span className="canvas-context-menu__item-label">Create Wait for</span>
        </button>

        {showCreateCampaign && (
          <button
            className="canvas-context-menu__item"
            onClick={() => finish(() => onCreateCampaign())}
          >
            <span className="canvas-context-menu__item-icon"><CampaignIcon /></span>
            <span className="canvas-context-menu__item-label">Create Campaign</span>
          </button>
        )}

        {clipboardAvailable && (
          <>
            <div className="canvas-context-menu__divider" />
            <button
              className="canvas-context-menu__item"
              onClick={() => finish(() => onPaste?.(placeRef.current))}
            >
              <span className="canvas-context-menu__item-icon"><ClipboardIcon /></span>
              <span className="canvas-context-menu__item-label">Paste Here</span>
              <span className="canvas-context-menu__item-shortcut">Ctrl+V</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(menu, document.body);
});

export default CanvasContextMenu;
